import axios, { AxiosInstance, AxiosError } from 'axios';

// Get backend URL from environment variable or use default
// Backend runs on port 6000 by default
const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:6000';

// Log API configuration in development
if (import.meta.env.DEV) {
  console.log('[API] Backend URL:', API_BASE_URL);
  console.log('[API] Full API Base:', `${API_BASE_URL}/api`);
}

// Create axios instance with default config
export const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor to add auth token and log requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('palmpay_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log requests in dev mode
    if (import.meta.env.DEV) {
      console.log('[API] →', config.method?.toUpperCase(), config.url, config.data ? 'with data:' : '');
      if (config.data) {
        // Don't log password in production, but show it in dev for debugging
        const logData = { ...config.data };
        if (logData.password) {
          logData.password = '***';
        }
        console.log('[API] Request data:', logData);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Log successful responses in dev mode
    if (import.meta.env.DEV) {
      console.log('[API] ✅ Success:', response.config.method?.toUpperCase(), response.config.url, response.status);
    }
    return response;
  },
  (error: AxiosError) => {
    // Enhanced error logging
    if (import.meta.env.DEV) {
      console.error('[API] ❌ Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: (error.response?.data as any)?.message || error.message,
        data: error.response?.data
      });
    }

    // Handle network errors (no response from server)
    if (!error.response) {
      console.error('[API] Network Error - Server not reachable');
      return Promise.reject({
        ...error,
        isNetworkError: true,
        message: 'Cannot connect to server. Please check if the backend is running on port 6000.',
        userMessage: 'Cannot connect to server. Please check if the backend is running.'
      });
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('palmpay_token');
      localStorage.removeItem('palmpay_user');
      if (!window.location.pathname.includes('/auth')) {
        window.location.href = '/auth';
      }
    }

    return Promise.reject(error);
  }
);

export default api;

